import {
  getList,
  getObject,
  getOptionalObject,
} from "@surya-digital/leo-ts-types";
import { SingleChoiceOption, SingleChoiceOptionDTO } from "./singleChoiceOption";
import { Code, CodeDTO } from "./code";
import { GridSingleChoiceDisplayTypeV1 } from "./gridSingleChoiceDisplayTypeV1";

export interface GridSingleChoiceQuestionV1DTO {
  options: SingleChoiceOptionDTO[];
  defaultOptionCode?: CodeDTO | null;
  singleChoiceDisplayType: GridSingleChoiceDisplayTypeV1.GridSingleChoiceDisplayTypeV1DTO;
}

export class GridSingleChoiceQuestionV1 {
  readonly isGridSingleChoiceQuestionV1GridSingleChoiceQuestionV1: boolean = true;

  readonly options: SingleChoiceOption[];
  readonly defaultOptionCode: Code | null;
  readonly singleChoiceDisplayType: GridSingleChoiceDisplayTypeV1.GridSingleChoiceDisplayTypeV1;

  static readonly constraints = {
    "options": {
      "minSize": 1,
      "maxSize": 32,
    },
  }

  constructor(
  options: SingleChoiceOption[], 
  defaultOptionCode: Code | null = null, 
  singleChoiceDisplayType: GridSingleChoiceDisplayTypeV1.GridSingleChoiceDisplayTypeV1
  ) {
    GridSingleChoiceQuestionV1.validateOptions(options);
    this.options = options;
    this.defaultOptionCode = defaultOptionCode;
    this.singleChoiceDisplayType = singleChoiceDisplayType;
  }

  static fromDTO(dto: GridSingleChoiceQuestionV1DTO): GridSingleChoiceQuestionV1 {
    const options = getList<SingleChoiceOptionDTO, SingleChoiceOption>(
      dto,
      "options",
      SingleChoiceOption.fromDTO,
      InvalidGridSingleChoiceQuestionV1OptionsError
    );
    let defaultOptionCode = getOptionalObject(dto, "defaultOptionCode");
    if(defaultOptionCode !== undefined && defaultOptionCode !== null) {
      defaultOptionCode = Code.fromDTO(getObject(dto, "defaultOptionCode", InvalidGridSingleChoiceQuestionV1DefaultOptionCodeError));
    }
    const singleChoiceDisplayType = GridSingleChoiceDisplayTypeV1.fromDTO(getObject(dto, "singleChoiceDisplayType", InvalidGridSingleChoiceQuestionV1SingleChoiceDisplayTypeError));
    return new GridSingleChoiceQuestionV1(
      options,
      defaultOptionCode,
      singleChoiceDisplayType
    );
  }

  toDTO(): GridSingleChoiceQuestionV1DTO {
    const dto: GridSingleChoiceQuestionV1DTO = {
        options: this.options.map((e: SingleChoiceOption) => { return e.toDTO() }),
        singleChoiceDisplayType: this.singleChoiceDisplayType.toDTO()
    };
    if (this.defaultOptionCode !== undefined && this.defaultOptionCode !== null){
      dto.defaultOptionCode = this.defaultOptionCode.toDTO();
    }
    return dto;
  }
  private static validateOptions = (options: SingleChoiceOption[]): void => {
    if(options.length < 1) {
      throw new InvalidGridSingleChoiceQuestionV1OptionsMinSizeError(
        1,
        `Attribute options is too short. Size is ${options.length}. Min size is 1.`
      );
    }
    if(options.length > 32) {
      throw new InvalidGridSingleChoiceQuestionV1OptionsMaxSizeError(
        32,
        `Attribute options is too long. Size is ${options.length}. Max size is 32.`
      );
    }
  }
  
}

GridSingleChoiceQuestionV1.prototype.toString = function toString(): string {
  return `GridSingleChoiceQuestionV1(options='${this.options}',defaultOptionCode='${this.defaultOptionCode}',singleChoiceDisplayType='${this.singleChoiceDisplayType}')`;
};

export class InvalidGridSingleChoiceQuestionV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1Error";
  }
}
export class InvalidGridSingleChoiceQuestionV1OptionsError extends InvalidGridSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1OptionsError";
  }
}

export class InvalidGridSingleChoiceQuestionV1OptionsMaxSizeError extends InvalidGridSingleChoiceQuestionV1OptionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1OptionsMaxSizeError";
  }
}

export class InvalidGridSingleChoiceQuestionV1OptionsMinSizeError extends InvalidGridSingleChoiceQuestionV1OptionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1OptionsMinSizeError";
  }
}

export class InvalidGridSingleChoiceQuestionV1DefaultOptionCodeError extends InvalidGridSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1DefaultOptionCodeError";
  }
}

export class InvalidGridSingleChoiceQuestionV1SingleChoiceDisplayTypeError extends InvalidGridSingleChoiceQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridSingleChoiceQuestionV1SingleChoiceDisplayTypeError";
  }
}
